import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Calendar, FileText, Cpu, ShieldCheck, Gauge, Globe } from "lucide-react";
import { Link } from "react-router-dom";

import cc1Pro from "@/assets/robots/cc1-pro-main.jpg";
import mt1Max from "@/assets/robots/mt1-max-hero.jpeg";
import bellabotProAsset from "@/assets/robots/bellabot-pro-product.png.asset.json";
import kettybotPro from "@/assets/robots/kettybot-pro.jpg";
import t600 from "@/assets/robots/pudu-t600-updated.png";
import sh1 from "@/assets/robots/sh1-main.png";
import holabot from "@/assets/robots/holabot.png";
import flashbotMax from "@/assets/robots/flashbot-max-new.jpeg";

type Robot = {
  name: string;
  tagline: string;
  category: string;
  image: string;
  bestFor: string;
  industries: string;
  specs: { label: string; value: string }[];
  quoteHref: string;
  demoHref: string;
};

const ROBOTS: Robot[] = [
  {
    name: "PUDU CC1 Pro",
    tagline: "Autonomous Cleaning Robot",
    category: "Commercial Cleaning",
    image: cc1Pro,
    bestFor: "Offices, retail, hospitality floors",
    industries: "Retail · Office · Hotels · Healthcare",
    specs: [
      { label: "Runtime", value: "Up to 8 hrs" },
      { label: "Coverage", value: "1,200 m²/h" },
      { label: "Navigation", value: "LiDAR + Vision" },
      { label: "Functions", value: "Sweep · Scrub · Mop" },
    ],
    quoteHref: "/robots/cc1-pro",
    demoHref: "https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics",
  },
  {
    name: "PUDU MT1 Max",
    tagline: "AI-Powered Sweeper & Vacuum",
    category: "Industrial Cleaning",
    image: mt1Max,
    bestFor: "Warehouses, factories, large facilities",
    industries: "Warehousing · Manufacturing · Logistics",
    specs: [
      { label: "Coverage", value: "10,000+ m²/h" },
      { label: "Runtime", value: "24/7 Capable" },
      { label: "Navigation", value: "3D LiDAR" },
      { label: "Rating", value: "IPX6 Water Resistant" },
    ],
    quoteHref: "/robots/mt1-max",
    demoHref: "https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics",
  },
  {
    name: "BellaBot Pro",
    tagline: "Delivery & Advertising Robot",
    category: "Commercial Delivery",
    image: bellabotProAsset.url,
    bestFor: "Restaurants, cafés, banquet halls",
    industries: "Restaurants · Hospitality · Senior Living",
    specs: [
      { label: "Payload", value: "40 kg total" },
      { label: "Trays", value: "4 tiers" },
      { label: "Runtime", value: "12–24 hrs" },
      { label: "Display", value: "Interactive face" },
    ],
    quoteHref: "/robots/bellabot-pro",
    demoHref: "https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics",
  },
  {
    name: "KettyBot Pro",
    tagline: "Marketing & Guide Robot",
    category: "Commercial Delivery",
    image: kettybotPro,
    bestFor: "Hosting, guiding, upselling guests",
    industries: "Restaurants · Retail · Hotels · Events",
    specs: [
      { label: "Display", value: "18.5\" HD ad screen" },
      { label: "Payload", value: "15 kg" },
      { label: "Runtime", value: "Up to 20 hrs" },
      { label: "Modes", value: "Greet · Guide · Deliver" },
    ],
    quoteHref: "/robots/kettybot-pro",
    demoHref: "https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics",
  },
  {
    name: "PUDU T600",
    tagline: "Industrial Delivery Robot",
    category: "Industrial Delivery",
    image: t600,
    bestFor: "Heavy material transport",
    industries: "Manufacturing · Warehousing · Logistics",
    specs: [
      { label: "Payload", value: "600 kg" },
      { label: "Positioning", value: "±10 mm" },
      { label: "Runtime", value: "10+ hrs" },
      { label: "Safety", value: "360° protection" },
    ],
    quoteHref: "/robots/pudu-t600",
    demoHref: "https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics",
  },
  {
    name: "PUDU SH1",
    tagline: "Smart Tray Delivery Robot",
    category: "Commercial Cleaning",
    image: sh1,
    bestFor: "Premium hospitality service",
    industries: "Restaurants · Hotels · Casinos",
    specs: [
      { label: "Trays", value: "Multi-tier modular" },
      { label: "Runtime", value: "12 hrs" },
      { label: "Navigation", value: "PUDU SLAM 3.0" },
      { label: "Mode", value: "Direct & Bussing" },
    ],
    quoteHref: "/robots/sh1",
    demoHref: "https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics",
  },
  {
    name: "HolaBot",
    tagline: "Heavy-Duty Bussing Robot",
    category: "Commercial Delivery",
    image: holabot,
    bestFor: "Bus tubs, dish return, kitchens",
    industries: "Restaurants · Banquet · Cafeterias",
    specs: [
      { label: "Payload", value: "60 kg" },
      { label: "Voice", value: "Call-to-table" },
      { label: "Suspension", value: "Shock-absorbing" },
      { label: "Runtime", value: "Up to 23 hrs" },
    ],
    quoteHref: "/robots/holabot",
    demoHref: "https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics",
  },
  {
    name: "FlashBot Max",
    tagline: "Multi-Floor Delivery Robot",
    category: "Commercial Delivery",
    image: flashbotMax,
    bestFor: "Elevators, multi-floor buildings",
    industries: "Hotels · Hospitals · Office Towers",
    specs: [
      { label: "Capacity", value: "Large modular cabin" },
      { label: "Elevator", value: "IoT integration" },
      { label: "Runtime", value: "12+ hrs" },
      { label: "Voice", value: "Multilingual AI" },
    ],
    quoteHref: "/robots/flashbot-max",
    demoHref: "https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics",
  },
];

const TRUST_STATS = [
  { icon: Cpu, label: "AI-Driven", sub: "Performance" },
  { icon: ShieldCheck, label: "99.99%", sub: "Reliability" },
  { icon: Gauge, label: "24/7", sub: "Operation" },
  { icon: Globe, label: "Global", sub: "Support" },
];

const RobotShowcase = () => {
  const [index, setIndex] = useState(0);
  const robot = ROBOTS[index];

  const prev = () => setIndex((i) => (i - 1 + ROBOTS.length) % ROBOTS.length);
  const next = () => setIndex((i) => (i + 1) % ROBOTS.length);

  return (
    <section
      className="relative my-12 rounded-3xl overflow-hidden border border-[hsl(var(--line))]"
      style={{
        background: `
          radial-gradient(900px 600px at 20% 10%, hsl(210 100% 30% / 0.18) 0%, transparent 60%),
          radial-gradient(900px 600px at 85% 90%, hsl(43 70% 45% / 0.10) 0%, transparent 60%),
          linear-gradient(180deg, hsl(210 83% 6%) 0%, hsl(210 83% 3%) 100%)
        `,
      }}
    >
      {/* Header */}
      <div className="text-center pt-10 sm:pt-14 px-4">
        <span className="inline-block text-[10px] sm:text-xs tracking-[0.3em] text-secondary font-semibold mb-3">
          INTERACTIVE ROBOT SHOWCASE
        </span>
        <h2 className="font-orbitron font-bold text-3xl sm:text-4xl md:text-5xl mb-3 leading-tight">
          MEET THE NEIL AI{" "}
          <span className="bg-gradient-to-r from-[hsl(43,75%,55%)] to-[hsl(43,90%,65%)] bg-clip-text text-transparent">
            ROBOTICS LINEUP
          </span>
        </h2>
        <p className="text-foreground font-semibold text-base sm:text-lg mb-1">
          Intelligent Robots. Real Results.
        </p>
        <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto">
          From cleaning and delivery to marketing and industrial transport — our robots work 24/7 so your team can focus on growth.
        </p>
      </div>

      {/* Main stage */}
      <div className="grid lg:grid-cols-[1fr_1.1fr] gap-6 lg:gap-10 px-4 sm:px-8 lg:px-12 py-10 lg:py-14 items-center">
        {/* Spec panel */}
        <div className="order-2 lg:order-1 space-y-5">
          <div>
            <span className="inline-block text-xs font-semibold text-secondary uppercase tracking-wider mb-2">
              {robot.category}
            </span>
            <h3 className="font-orbitron font-bold text-2xl sm:text-3xl text-foreground mb-1">
              {robot.name}
            </h3>
            <p className="text-secondary/90 font-medium text-sm sm:text-base">
              {robot.tagline}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {robot.specs.map((s) => (
              <div
                key={s.label}
                className="rounded-xl border border-[hsl(var(--line))] bg-black/40 backdrop-blur-sm p-3"
              >
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                  {s.label}
                </div>
                <div className="text-sm font-semibold text-foreground">{s.value}</div>
              </div>
            ))}
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex gap-2">
              <span className="text-secondary font-semibold min-w-[80px]">Best for:</span>
              <span className="text-muted-foreground">{robot.bestFor}</span>
            </div>
            <div className="flex gap-2">
              <span className="text-secondary font-semibold min-w-[80px]">Industries:</span>
              <span className="text-muted-foreground">{robot.industries}</span>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button
              asChild
              size="lg"
              className="bg-gradient-to-r from-[hsl(43,75%,55%)] to-[hsl(43,90%,65%)] text-black font-bold hover:opacity-90"
            >
              <a href={robot.demoHref} target="_blank" rel="noopener noreferrer">
                <Calendar className="mr-2 w-4 h-4" />
                Book Demo
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-primary/50 text-primary hover:bg-primary/10"
            >
              <Link to={robot.quoteHref}>
                <FileText className="mr-2 w-4 h-4" />
                Request Quote
              </Link>
            </Button>
          </div>
        </div>

        {/* Robot stage */}
        <div className="order-1 lg:order-2 relative">
          <div
            className="relative aspect-square sm:aspect-[4/3] rounded-2xl overflow-hidden border border-primary/20"
            style={{
              background:
                "radial-gradient(ellipse at 50% 85%, hsl(195 100% 35% / 0.25) 0%, transparent 70%), linear-gradient(180deg, hsl(210 83% 4%) 0%, hsl(210 83% 2%) 100%)",
            }}
          >
            {/* Glow floor */}
            <div
              className="absolute bottom-0 left-1/2 -translate-x-1/2 w-3/4 h-24 rounded-full blur-3xl opacity-60"
              style={{ background: "radial-gradient(ellipse, hsl(195 100% 50% / 0.5), transparent 70%)" }}
            />

            <img
              src={robot.image}
              alt={`${robot.name} — ${robot.tagline}`}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-contain p-6 sm:p-10 drop-shadow-[0_20px_40px_rgba(0,229,204,0.25)] animate-fade-in"
              key={robot.name}
            />

            {/* Floating name badge */}
            <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-md border border-[hsl(var(--line))]">
              <span className="text-[10px] sm:text-xs font-semibold text-foreground tracking-wide">
                {String(index + 1).padStart(2, "0")} / {String(ROBOTS.length).padStart(2, "0")}
              </span>
            </div>

            {/* Prev/Next */}
            <button
              onClick={prev}
              aria-label="Previous robot"
              className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-black/60 backdrop-blur-md border border-primary/30 text-primary hover:bg-primary/20 transition flex items-center justify-center"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={next}
              aria-label="Next robot"
              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-black/60 backdrop-blur-md border border-primary/30 text-primary hover:bg-primary/20 transition flex items-center justify-center"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Thumbnail selector */}
          <div className="mt-4 grid grid-cols-4 sm:grid-cols-8 gap-2">
            {ROBOTS.map((r, i) => (
              <button
                key={r.name}
                onClick={() => setIndex(i)}
                aria-label={`Select ${r.name}`}
                className={`relative aspect-square rounded-lg overflow-hidden border-2 transition ${
                  i === index
                    ? "border-secondary shadow-[0_0_20px_hsl(43,75%,55%,0.4)]"
                    : "border-[hsl(var(--line))] opacity-60 hover:opacity-100"
                }`}
              >
                <img src={r.image} alt={r.name} loading="lazy" className="absolute inset-0 w-full h-full object-contain p-1 bg-black/40" />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Trust strip */}
      <div className="border-t border-[hsl(var(--line))] bg-black/30 backdrop-blur-sm">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[hsl(var(--line))]">
          {TRUST_STATS.map((s) => {
            const Icon = s.icon;
            return (
              <div
                key={s.label}
                className="flex items-center justify-center gap-3 py-4 px-3 bg-[hsl(210,83%,5%)]"
              >
                <Icon className="w-5 h-5 text-secondary" />
                <div className="text-left">
                  <div className="text-sm font-bold text-foreground">{s.label}</div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {s.sub}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer CTAs */}
      <div className="text-center px-4 py-8">
        <p className="text-[10px] sm:text-xs tracking-[0.4em] text-muted-foreground mb-4">
          AUTOMATE. ELEVATE. <span className="text-secondary">DOMINATE.</span>
        </p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Button asChild size="lg" variant="outline" className="border-primary/40 text-primary hover:bg-primary/10">
            <Link to="/robots">View All Robots</Link>
          </Button>
          <Button asChild size="lg" className="bg-gradient-to-r from-[hsl(43,75%,55%)] to-[hsl(43,90%,65%)] text-black font-bold">
            <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
              Book Facility Assessment
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default RobotShowcase;
